<template>
  <div>
    <helpers-section-header class="mt-6 mb-8"> Trending Signal Rules </helpers-section-header>
    <v-row density="compact">
      <v-col cols="12" md="7">
        <p>
          Create rules filtering stock selections based on price/volume trends. Trending rules are
          placed in groups. Within each group you can decide if you want all trends to match for a
          stock to be selected or have stocks selected as long as at least one trend matches.
        </p>
        <p>
          Create multiple groups and decide if all of your groups must match for a stock to be
          selected or a stock is selected as long as at least one group matches.
        </p>
      </v-col>
      <v-col cols="12" md="7">
        <v-checkbox
          v-model="criteria.and_all_rules"
          label="If checked then all rule groups must match a signal, otherwise just one of your groups needs to match"
          hide-details
        />
      </v-col>
    </v-row>

    <v-row density="compact">
      <v-col cols="12">
        <v-dialog v-model="groupDialog" fullscreen>
          <template #activator="{ props }">
            <v-btn color="primary" dark class="mb-4" v-bind="props"> Create New Rule Group </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ groupTitle }}</span>
            </v-card-title>

            <v-card-text>
              <v-row density="compact">
                <v-col cols="12" sm="12">
                  <v-checkbox v-model="editedGroup.enabled" density="compact" label="Enabled" />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedGroup.description"
                    hide-details
                    clearable
                    autofocus
                    label="Group Description"
                    variant="outlined"
                    autocorrect="off"
                    spellcheck="false"
                    class="pt-0"
                    style="max-width: 400px"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <v-select
                    v-model="editedGroup.and_or"
                    :items="[
                      {
                        id: 1,
                        description:
                          'All rules within the group must match for a stock to be chosen'
                      },
                      { id: 2, description: 'If any rule matches then the stock will be chosen' }
                    ]"
                    item-title="description"
                    item-value="id"
                    label="How are stocks matched?"
                    density="compact"
                    hide-details
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12">
                  <v-data-table
                    density="compact"
                    items-per-page="-1"
                    hide-default-footer
                    :headers="metric_headers"
                    :items="editedGroup.rules"
                  >
                    <template #top>
                      <v-dialog v-model="metricDialog" persistent scrollable max-width="650px">
                        <template #activator="{ props }">
                          <v-btn
                            color="primary"
                            style="max-width: 350px"
                            theme="dark"
                            class="mb-4"
                            v-bind="props"
                          >
                            Add New Trending Rule to Group
                          </v-btn>
                        </template>
                        <v-card>
                          <v-card-title>
                            <span class="text-h5">{{ metricTitle }}</span>
                          </v-card-title>

                          <v-card-text style="height: 400px">
                            <v-container>
                              <v-row density="compact">
                                <v-col cols="12" sm="12">
                                  <v-checkbox
                                    v-model="editedMetric.enabled"
                                    density="compact"
                                    label="Rule Enabled"
                                  />
                                </v-col>
                                <v-col cols="12" sm="12">
                                  <v-autocomplete
                                    v-model="editedMetric.trend"
                                    :items="trends"
                                    label="Choose a Trend to Add"
                                    item-title="name"
                                    item-value="id"
                                    density="compact"
                                    hide-details
                                    variant="outlined"
                                    @update:model-value="UpdateComponent"
                                  />
                                </v-col>
                              </v-row>
                              <component :is="selected_component" v-model:rule="editedMetric" />
                            </v-container>
                          </v-card-text>

                          <v-card-actions>
                            <div class="flex-grow-1" />
                            <v-btn color="blue-darken-1" text @click="closeMetric"> Cancel </v-btn>
                            <v-btn color="blue-darken-1" text @click="saveMetric"> Save </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </template>
                    <template #item.metric_description="{ item }">
                      {{ LookupTrend(item.trend) }}

                      <SummarySandP v-if="item.trend == 'perf_vs_sp'" :rule="item" />
                      <SummaryAdx v-if="item.trend == 'adx'" :rule="item" />
                      <SummaryAvgRanking v-if="item.trend == '13f_ranking'" :rule="item" />
                      <SummaryBollinger v-if="item.trend == 'bb'" :rule="item" />
                      <SummaryBreakout v-if="item.trend == 'breakout'" :rule="item" />
                      <SummaryHighLow v-if="item.trend == 'high'" :rule="item" />
                      <SummaryKama v-if="item.trend == 'kama'" :rule="item" />
                      <SummaryMa v-if="item.trend == 'ma'" :rule="item" />
                      <SummaryOverbought v-if="item.trend == 'overbought'" :rule="item" />
                      <SummaryOversold v-if="item.trend == 'oversold'" :rule="item" />
                      <SummaryBeta v-if="item.trend == 'beta'" :rule="item" />
                      <SummaryRSI v-if="item.trend == 'rsi'" :rule="item" />
                      <SummaryMacd v-if="item.trend == 'macd'" :rule="item" />
                      <SummaryEma v-if="item.trend == 'ema'" :rule="item" />
                      <SummarySector v-if="item.trend == 'sector'" :rule="item" />
                      <SummaryIchimoku v-if="item.trend == 'ichimoku'" :rule="item" />
                      <SummaryOptions v-if="item.trend == 'options'" :rule="item" />
                    </template>
                    <template #item.action="{ item }">
                      <v-icon small class="mr-2" @click="editMetric(item)">
                        {{ mdiPencil }}
                      </v-icon>
                      <v-icon small @click="deleteMetric(item)">
                        {{ mdiDelete }}
                      </v-icon>
                    </template>

                    <template #item.enabled="{ item }">
                      <v-switch v-model="item.enabled" color="primary" />
                    </template>
                    <template #no-data>
                      No trending rules configure. Please click the "Add New Trending Rule to Group"
                      button to add one
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" variant="outlined" @click="closeGroup"> Cancel </v-btn>
              <v-btn color="primary" variant="outlined" @click="saveGroup"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>

      <v-col v-for="(group, index) in criteria.rules" :key="'group' + index" cols="12">
        <v-card>
          <!-- Toolbar -->
          <v-toolbar theme="dark" color="grey-darken-3">
            <v-toolbar-title>
              <span>{{ group.description ? group.description : 'Trend Group' }}</span>
            </v-toolbar-title>
            <v-spacer />

            <v-switch v-model="group.enabled" color="primary" class="vertical-align mr-3" hide-details />
            <v-icon class="mr-3" title="Edit Group" @click="editGroup(group)">
              {{ mdiPencil }}
            </v-icon>
            <v-icon title="Delete Group" @click="deleteGroup(group)">
              {{ mdiDelete }}
            </v-icon>
          </v-toolbar>
          <v-divider />
          <v-card-text>
            <span v-if="group.and_or == 1">All rules must match</span>
            <span v-else-if="group.and_or == 2">Any rule matches</span>
            <v-table>
              <tbody>
                <tr v-for="(rule, idx) in group.rules" :key="'rule' + idx">
                  <td>
                    <v-switch v-model="rule.enabled" color="primary" />
                  </td>
                  <td>
                    {{ LookupTrend(rule.trend) }}
                    <SummarySandP v-if="rule.trend == 'perf_vs_sp'" :rule="rule" />
                    <SummaryAdx v-if="rule.trend == 'adx'" :rule="rule" />
                    <SummaryAvgRanking v-if="rule.trend == '13f_ranking'" :rule="rule" />
                    <SummaryBollinger v-if="rule.trend == 'bb'" :rule="rule" />
                    <SummaryBreakout v-if="rule.trend == 'breakout'" :rule="rule" />
                    <SummaryHighLow v-if="rule.trend == 'high'" :rule="rule" />
                    <SummaryKama v-if="rule.trend == 'kama'" :rule="rule" />
                    <SummaryMa v-if="rule.trend == 'ma'" :rule="rule" />
                    <SummaryOverbought v-if="rule.trend == 'overbought'" :rule="rule" />
                    <SummaryOversold v-if="rule.trend == 'oversold'" :rule="rule" />
                    <SummaryBeta v-if="rule.trend == 'beta'" :rule="rule" />
                    <SummaryRSI v-if="rule.trend == 'rsi'" :rule="rule" />
                    <SummaryMacd v-if="rule.trend == 'macd'" :rule="rule" />
                    <SummaryEma v-if="rule.trend == 'ema'" :rule="rule" />
                    <SummarySector v-if="rule.trend == 'sector'" :rule="rule" />
                    <SummaryIchimoku v-if="rule.trend == 'ichimoku'" :rule="rule" />
                    <SummaryOptions v-if="rule.trend == 'options'" :rule="rule" />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// Modules
import { mapWritableState } from 'pinia'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import Breakout from './trends/_TrendBreakout'
import SandP from './trends/_TrendSandP'
import MovingAverage from './trends/_TrendMa'
import Overbought from './trends/_TrendOverbought'
import Oversold from './trends/_TrendOversold'
import AvgRanking from './trends/_TrendAvgRanking'
import Kama from './trends/_TrendKama'
import Bollinger from './trends/_TrendBollinger'
import Adx from './trends/_TrendAdx'
import HighLow from './trends/_TrendHighLow'
import Beta from './trends/_TrendBeta'
import RSI from './trends/_TrendRSI'
import MACD from './trends/_TrendMacd'
import EMA from './trends/_TrendEma'
import Sector from './trends/_TrendSector'
import Ichimoku from './trends/_TrendIchimoku'
import Options from './trends/_TrendOptions'
import SummarySandP from './trends/_SummarySandP'
import SummaryAdx from './trends/_SummaryAdx'
import SummaryAvgRanking from './trends/_SummaryAvgRanking'
import SummaryBollinger from './trends/_SummaryBollinger'
import SummaryBreakout from './trends/_SummaryBreakout'
import SummaryHighLow from './trends/_SummaryHighLow'
import SummaryKama from './trends/_SummaryKama'
import SummaryMa from './trends/_SummaryMa'
import SummaryOverbought from './trends/_SummaryOverbought'
import SummaryOversold from './trends/_SummaryOversold'
import SummaryBeta from './trends/_SummaryBeta'
import SummaryRSI from './trends/_SummaryRSI'
import SummaryMacd from './trends/_SummaryMacd'
import SummaryEma from './trends/_SummaryEma'
import SummarySector from './trends/_SummarySector'
import SummaryIchimoku from './trends/_SummaryIchimoku'
import SummaryOptions from './trends/_SummaryOptions'
import { mdiDelete, mdiPen, mdiPencil } from '@mdi/js'
import { markRaw } from 'vue'
export default {
  components: {
    SummarySandP: markRaw(SummarySandP),
    SummaryAdx: markRaw(SummaryAdx),
    SummaryAvgRanking: markRaw(SummaryAvgRanking),
    SummaryBollinger: markRaw(SummaryBollinger),
    SummaryBreakout: markRaw(SummaryBreakout),
    SummaryHighLow: markRaw(SummaryHighLow),
    SummaryKama: markRaw(SummaryKama),
    SummaryMa: markRaw(SummaryMa),
    SummaryOverbought: markRaw(SummaryOverbought),
    SummaryOversold: markRaw(SummaryOversold),
    SummaryBeta: markRaw(SummaryBeta),
    SummaryRSI: markRaw(SummaryRSI),
    SummaryMacd: markRaw(SummaryMacd),
    SummaryEma: markRaw,
    SummarySector: markRaw(SummarySector),
    SummaryIchimoku: markRaw(SummaryIchimoku),
    SummaryOptions: markRaw(SummaryOptions)
    /*Breakout: markRaw(Breakout),
    SandP: markRaw(SandP),
    MovingAverage: markRaw(MovingAverage),  */
  },
  data() {
    return {
      mdiDelete,
      mdiPen,
      mdiPencil,
      groupBeingEdited: null,
      rename: {
        active: false,
        newName: '',
        saving: false
      },
      selected_component: null,
      metricDialog: false,
      groupDialog: false,
      comparison_options: [
        { id: 1, description: 'Greater Than' },
        { id: 2, description: 'Less Than' },
        { id: 3, description: 'Between' }
      ],

      group_headers: [
        {
          title: 'Group Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled',
          class: 'align-top'
        },
        {
          title: 'Group Description',
          align: 'left',
          sortable: false,
          key: 'group_description'
        },
        { title: 'Actions', key: 'action', sortable: false, class: 'align-top' }
      ],
      metric_headers: [
        {
          title: 'Rule Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Description',
          align: 'left',
          sortable: false,
          key: 'metric_description'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      defaultRule: { enabled: true, and_or: 1, rules: [], description: 'Trend Group' },
      editedGroup: { enabled: true, and_or: 1, rules: [], description: 'Trend Group' },
      editedIndex: -1,
      editedMetric: { trend: null, enabled: true, config: {} },
      editedMetricIndex: -1,
      trends: [
        { id: 'breakout', name: 'Pricing/Volume Channel Breakout' },
        { id: 'perf_vs_sp', name: 'Performance vs S&P' },
        { id: 'ma', name: 'Simple Moving Average' },
        { id: 'overbought', name: 'Short-Term Overbought' },
        { id: 'oversold', name: 'Short-Term Oversold' },
        { id: '13f_ranking', name: 'Avg 13F Ranking Breakout' },
        { id: 'kama', name: 'Kaufman Adaptive Moving Average (KAMA)' },
        { id: 'bb', name: 'Bollinger Bands' },
        { id: 'adx', name: 'Average Directional Index (ADX)' },
        { id: 'high', name: 'Trading below the high for a given timeframe' },
        { id: 'beta', name: 'Stock BETA' },
        { id: 'rsi', name: 'Relative Strength Index (RSI)' },
        { id: 'macd', name: 'Moving Average Convergence Divergence (MACD)' },
        { id: 'ema', name: 'Exponential Moving Average (EMA)' },
        { id: 'sector', name: 'Invest in or Avoid Oversold or Overbought Sectors' },
        { id: 'ichimoku', name: 'Ichimoku' },
        { id: 'options', name: 'Daily Aggregate Options (EXPERIMENTAL)' }
      ]
    }
  },
  computed: {
    groupTitle() {
      return this.editedIndex === -1 ? 'New Rule Group' : 'Edit Rule Group'
    },
    metricTitle() {
      return this.editedIndex === -1 ? 'New Trend Rule' : 'Edit Trend Rule'
    },
    ...mapWritableState(useAppStore, ['whaleTraderCriteria', 'signalView', 'exit_time_periods']),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    appcriteria() {
      return this.whaleTraderCriteria
    },
    criteria() {
      return this.whaleTraderCriteria.signals.trend_signal
    },
    enabled() {
      return this.whaleTraderCriteria.signals.trend_signal.enabled
    }
    /* ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.trend_signal',
      enabled: 'app.whaleTraderCriteria.signals.trend_signal.enabled',
      signalView: 'app.signalView',
      exit_time_periods: 'app.exit_time_periods'
    })*/
  },
  methods: {
    LookupTrend(trend_id) {
      var desc = ''

      for (var i = 0; i < this.trends.length; i++) {
        var rec = this.trends[i]
        if (rec.id == trend_id) {
          desc = rec.name
          break
        }
      }

      return desc
    },
    UpdateComponent() {
      this.selected_component = null
      if (this.editedMetric.trend && this.editedMetric.trend == 'breakout') {
        this.selected_component = markRaw(Breakout)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'perf_vs_sp') {
        this.selected_component = markRaw(SandP)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'ma') {
        this.selected_component = markRaw(MovingAverage)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'overbought') {
        this.selected_component = markRaw(Overbought)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'oversold') {
        this.selected_component = markRaw(Oversold)
      } else if (this.editedMetric.trend && this.editedMetric.trend == '13f_ranking') {
        this.selected_component = markRaw(AvgRanking)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'kama') {
        this.selected_component = markRaw(Kama)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'bb') {
        this.selected_component = markRaw(Bollinger)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'adx') {
        this.selected_component = markRaw(Adx)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'high') {
        this.selected_component = markRaw(HighLow)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'beta') {
        this.selected_component = markRaw(Beta)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'rsi') {
        this.selected_component = markRaw(RSI) //RSI
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'macd') {
        this.selected_component = markRaw(MACD)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'ema') {
        this.selected_component = markRaw(EMA)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'sector') {
        this.selected_component = markRaw(Sector)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'ichimoku') {
        this.selected_component = markRaw(Ichimoku)
      } else if (this.editedMetric.trend && this.editedMetric.trend == 'options') {
        this.selected_component = markRaw(Options)
      }
    },
    editGroup(item) {
      this.editedIndex = this.criteria.rules.indexOf(item)
      this.editedGroup = Object.assign({}, item)
      this.groupDialog = true
    },

    deleteGroup(item) {
      const index = this.criteria.rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') && this.criteria.rules.splice(index, 1)
    },

    closeGroup() {
      this.groupDialog = false
      setTimeout(() => {
        this.editedGroup = Object.assign({}, this.defaultRule)
        this.editedIndex = -1
      }, 300)
    },

    saveGroup() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.rules[this.editedIndex], this.editedGroup)
      } else {
        if (!this.criteria.rules) {
          this.criteria.rules = []
        }
        this.criteria.rules.push(this.editedGroup)
      }
      this.closeGroup()
    },

    editMetric(item) {
      this.editedMetricIndex = this.editedGroup.rules.indexOf(item)
      this.editedMetric = Object.assign({}, item)
      this.metricDialog = true
      this.UpdateComponent()
    },

    deleteMetric(item) {
      const index = this.editedGroup.rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.editedGroup.rules.splice(index, 1)
    },

    closeMetric() {
      this.metricDialog = false
      setTimeout(() => {
        this.editedMetric = Object.assign({}, this.defaultMetricRule)
        this.editedMetricIndex = -1
        this.selected_component = null
        this.editedMetric.enabled = true
        this.editedMetric.config = {}
      }, 300)
    },

    saveMetric() {
      if (this.editedMetricIndex > -1) {
        Object.assign(this.editedGroup.rules[this.editedMetricIndex], this.editedMetric)
      } else {
        if (!this.editedGroup.rules) {
          this.editedGroup.rules = []
        }
        this.editedGroup.rules.push(this.editedMetric)
      }
      this.closeMetric()
    },
    renameActivate(group) {
      this.groupBeingEdited = group
      this.rename.newName = group.description
      this.rename.active = true
    },

    renameSave(group) {
      this.groupBeingEdited.description = this.rename.newName
      this.rename.saving = false
      this.rename.active = false
      this.rename.newName = ''
      this.groupBeingEdited = null
    },

    renameCancel() {
      this.backtestBeingEdited = null
      this.rename.active = false
      this.rename.newName = ''
    }
  }
}
</script>
<style>
.align-top {
  vertical-align: top;
}
</style>
